/**
 * interceptors.ts - 请求/响应拦截器
 *
 * 功能说明：
 * - 请求拦截器：添加认证token、设备ID、设备信息
 * - 响应拦截器：处理认证错误、网络离线
 *
 * 依赖关系：
 * - 依赖 api/config.ts 的 axios 实例
 * - 依赖 store/authStore.ts
 * - 依赖 services/syncService.ts
 *
 * 关键注释索引：
 * - [请求拦截] - 见下方 api.interceptors.request.use
 * - [响应拦截] - 见下方 api.interceptors.response.use
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 */

import { api } from './config';
import { syncService } from '../syncService';
import { useAuthStore } from '../../store/authStore';

// ==================== 工具函数 ====================

/**
 * [浏览器检测] 检测是否在浏览器环境运行
 */
const isBrowser = (): boolean => typeof window !== 'undefined' && typeof navigator !== 'undefined';

/**
 * [设备ID管理] 获取或生成设备ID
 *
 * @description 用于操作记录审计追踪
 */
const getOrCreateDeviceId = (): string => {
  if (!isBrowser()) return '';
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = `web_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};

/**
 * [设备信息] 获取浏览器和系统信息
 */
const getDeviceInfo = (): { deviceType: string; os: string; browser: string } => {
  if (!isBrowser()) {
    return { deviceType: 'desktop', os: 'unknown', browser: 'unknown' };
  }
  const ua = navigator.userAgent;
  return {
    deviceType: /Mobi|Android/i.test(ua) ? 'mobile' : 'desktop',
    os: navigator.platform || 'unknown',
    browser: getBrowserName(ua),
  };
};

/**
 * [浏览器识别] 根据 User-Agent 获取浏览器名称
 */
const getBrowserName = (ua: string): string => {
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/Chrome\//i.test(ua) && !/Chromium\//i.test(ua)) return 'Chrome';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return 'Safari';
  if (/MSIE|Trident\//i.test(ua)) return 'IE';
  return 'Unknown';
};

/**
 * [获取本地Token] 从 localStorage 获取认证 token
 */
const getAuthStorageRaw = (): string | null =>
  isBrowser() ? localStorage.getItem('auth-storage') : null;

/**
 * [网络检测] 检测网络是否在线
 */
const isOnline = (): boolean => (isBrowser() ? navigator.onLine : true);

/**
 * [跳转登录] 重定向到登录页面
 */
const redirectToLogin = () => {
  if (!isBrowser()) return;
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// ==================== 权限同步 ====================

let isSyncingPermissions = false;
let isFetchPermissionSyncInstalled = false;

/**
 * [权限同步] 检查并同步用户权限
 *
 * @description 检测到服务端权限更新时，自动刷新本地权限
 */
const syncPermissionsIfNeeded = async (response: any) => {
  if (!isBrowser()) return;

  const headerValue =
    response?.headers?.['x-permissions-updated-at'] ||
    response?.headers?.get?.('x-permissions-updated-at');
  if (!headerValue) return;

  const serverPermissionsUpdatedAt = new Date(headerValue).getTime();
  if (Number.isNaN(serverPermissionsUpdatedAt)) return;

  const authStore = useAuthStore.getState();
  if (!authStore.isAuthenticated || !authStore.user || !authStore.token) return;

  const localPermissionsUpdatedAt = authStore.user.permissionsUpdatedAt || 0;
  if (serverPermissionsUpdatedAt <= localPermissionsUpdatedAt) return;
  if (isSyncingPermissions) return;

  isSyncingPermissions = true;
  try {
    console.log('[API] 检测到权限已更新，开始同步当前用户权限');
    await authStore.refreshUserPermissions();
  } finally {
    isSyncingPermissions = false;
  }
};

/**
 * [Fetch拦截] 安装 Fetch 权限同步
 *
 * @description 拦截全局 fetch，自动同步权限变更
 */
export const installFetchPermissionSync = () => {
  if (!isBrowser() || isFetchPermissionSyncInstalled || typeof window.fetch !== 'function') {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const response = await originalFetch(...args);

    try {
      await syncPermissionsIfNeeded(response);
    } catch (error) {
      console.error('[API] 同步请求权限失败:', error);
    }

    return response;
  };

  isFetchPermissionSyncInstalled = true;
};

// ==================== 请求拦截器 ====================

/**
 * [请求拦截] 添加认证token和设备信息
 *
 * @description 每个请求自动注入：
 * - Authorization: Bearer token
 * - X-Device-ID: 设备唯一标识
 * - X-Device-Info: 设备类型、操作系统、浏览器
 */
api.interceptors.request.use(
  (config) => {
    try {
      // [认证Token] 从 localStorage 获取并注入
      const authStorage = getAuthStorageRaw();
      if (authStorage) {
        const authState = JSON.parse(authStorage);
        const token = authState?.state?.token || authState?.token;

        if (token && token !== 'null' && token !== null) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn('[API] Token 无效:', token);
        }
      }

      // [设备ID] 注入设备ID用于审计追踪
      config.headers['X-Device-ID'] = getOrCreateDeviceId();

      // [设备信息] 注入设备信息
      const deviceInfo = getDeviceInfo();
      config.headers['X-Device-Info'] = JSON.stringify(deviceInfo);
    } catch (error) {
      console.error('[API] 获取认证token失败:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ==================== 响应拦截器 ====================

let isRefreshing = false; // 防止多个请求同时刷新 token

/**
 * [响应拦截] 处理响应和错误
 *
 * @description
 * - 自动同步权限变更
 * - 处理 401 未授权错误
 * - 网络离线时提示用户
 */
api.interceptors.response.use(
  async (response) => {
    await syncPermissionsIfNeeded(response);
    return response.data; // 直接返回响应数据
  },
  async (error) => {
    await syncPermissionsIfNeeded(error.response);

    // 提取友好的错误消息
    let userMessage = '网络请求失败，请稍后重试';
    if (error.response?.data?.message) {
      userMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      userMessage = error.response.data.error;
    } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      userMessage = '请求超时，请检查网络连接或稍后重试';
    } else if (error.code === 'ERR_NETWORK') {
      userMessage = '网络连接失败，请检查网络';
    } else if (error.message) {
      userMessage = error.message;
    }
    console.error('[API] 请求错误:', userMessage);

    // [401处理] 未授权错误处理
    if (error.response?.status === 401) {
      console.warn('[API] 收到 401 响应');

      const authStorage = getAuthStorageRaw();
      if (authStorage) {
        const authState = JSON.parse(authStorage);
        const token = authState?.state?.token || authState?.token;

        if (!token || token === 'null') {
          console.error('[API] Token 无效，跳转登录页');
          if (isBrowser()) {
            localStorage.removeItem('auth-storage');
          }
          redirectToLogin();
          return Promise.reject(error);
        }
      } else {
        console.error('[API] localStorage 中没有认证信息，跳转登录页');
        redirectToLogin();
        return Promise.reject(error);
      }

      // [Token刷新] 防止重复刷新
      if (isRefreshing) {
        console.log('[API] 正在刷新 token，等待...');
        return Promise.reject(error);
      }

      isRefreshing = true;
      console.log('[API] Token 可能过期，尝试刷新...');

      // 延迟后跳转登录（简化处理）
      setTimeout(() => {
        console.error('[API] Token 已失效，清除认证信息并跳转登录页');
        if (isBrowser()) {
          localStorage.removeItem('auth-storage');
        }
        redirectToLogin();
        isRefreshing = false;
      }, 100);

      return Promise.reject(error);
    }

    // [离线处理] 网络离线提示
    if (!isOnline() && syncService.getPendingCount() > 0) {
      console.log('[API] 网络离线，数据已缓存');
    }

    return Promise.reject(error);
  }
);

// ==================== 导出 ====================

export { redirectToLogin, isOnline };
